import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  PaintBrush, Lightning, CircleNotch, VideoCamera, CurrencyDollar,
  Image, FileText, Target
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });

const FORMAT_OPTIONS = [
  { value: 'product_promo', label: 'Product Promotion' },
  { value: 'catalog', label: 'Catalog Promotion' },
  { value: 'short_video', label: 'Short-Form Video (Reel/TikTok)' },
  { value: 'long_video', label: 'Long-Form Video (YouTube/FB)' },
];

export default function CreativeProPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [models, setModels] = useState(null);
  const [usage, setUsage] = useState(null);
  const [selectedModel, setSelectedModel] = useState('nano_banana');
  const [prompt, setPrompt] = useState('');
  const [format, setFormat] = useState('social_post');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  // Campaign builder
  const [campFormat, setCampFormat] = useState('product_promo');
  const [campProduct, setCampProduct] = useState('');
  const [campAudience, setCampAudience] = useState('');
  const [campPlatform, setCampPlatform] = useState('Meta');
  const [campGenerating, setCampGenerating] = useState(false);
  const [campBrief, setCampBrief] = useState(null);
  const [guardrails, setGuardrails] = useState(null);

  useEffect(() => {
    clientsAPI.list().then(r => { setClients(r.data); if (r.data.length > 0) setSelectedClient(r.data[0].id); });
    axios.get(`${API}/creative-pro/models`, auth()).then(r => setModels(r.data)).catch(() => {});
    axios.get(`${API}/creative-pro/usage`, auth()).then(r => setUsage(r.data)).catch(() => {});
    axios.get(`${API}/guardrails/status`, auth()).then(r => setGuardrails(r.data)).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/creative-pro/generate`, {
        client_id: selectedClient, prompt, model: selectedModel, format, industry: ''
      }, auth());
      setResult(res.data);
      toast.success(`Generated with ${res.data.model_name} ($${res.data.cost})`);
      axios.get(`${API}/creative-pro/usage`, auth()).then(r => setUsage(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleCampaignBrief = async () => {
    if (!campProduct.trim()) return;
    setCampGenerating(true);
    try {
      const res = await axios.post(`${API}/campaigns/prompt-builder`, {
        client_id: selectedClient, campaign_format: campFormat,
        product_description: campProduct, target_audience: campAudience, platform: campPlatform,
      }, auth());
      setCampBrief(res.data);
      toast.success('Campaign brief generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCampGenerating(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="creative-pro-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">Creative Lab Pro</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Advanced model selection, campaign briefs & video agents</p>
          </div>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Usage + Guardrails */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="nx-metric-card rounded-lg">
            <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Images This Month</p>
            <p className="text-2xl font-bold text-[var(--accent)] font-mono">{usage?.images_generated || 0}</p>
          </div>
          <div className="nx-metric-card rounded-lg">
            <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Creative Spend</p>
            <p className="text-2xl font-bold text-[var(--success)] font-mono">${usage?.total_cost || 0}</p>
          </div>
          {guardrails && (
            <>
              <div className="nx-metric-card rounded-lg">
                <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Budget Used</p>
                <p className={`text-2xl font-bold font-mono ${guardrails.usage_percent > 80 ? 'text-[var(--error)]' : 'text-[var(--text-primary)]'}`}>{guardrails.usage_percent}%</p>
              </div>
              <div className="nx-metric-card rounded-lg">
                <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Guardrail (90%)</p>
                <p className={`text-2xl font-bold font-mono ${guardrails.guardrail_triggered ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>
                  {guardrails.guardrail_triggered ? 'TRIGGERED' : 'OK'}
                </p>
              </div>
            </>
          )}
        </div>

        <Tabs defaultValue="generate" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="generate" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <PaintBrush size={14} className="mr-1.5" /> Pro Generate
            </TabsTrigger>
            <TabsTrigger value="campaign" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <Target size={14} className="mr-1.5" /> Campaign Builder
            </TabsTrigger>
            <TabsTrigger value="models" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <PaintBrush size={14} className="mr-1.5" /> Models & Video
            </TabsTrigger>
          </TabsList>

          {/* Generate */}
          <TabsContent value="generate" className="space-y-4">
            <div className="nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Pro Creative Generator</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Model</label>
                    <Select value={selectedModel} onValueChange={setSelectedModel}>
                      <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        {models && Object.entries(models.models || {}).map(([k, v]) => (
                          <SelectItem key={k} value={k} disabled={v.status !== 'live'} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">
                            {v.name} — ${v.price_per_image}/img {v.status !== 'live' && '(Coming Soon)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Format</label>
                    <Select value={format} onValueChange={setFormat}>
                      <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        {['social_post', 'banner', 'story', 'display_ad', 'marketplace_listing'].map(f => (
                          <SelectItem key={f} value={f} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)] capitalize">{f.replace(/_/g, ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <textarea data-testid="pro-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} rows={3}
                  placeholder="Describe your creative... e.g., 'Premium wireless earbuds product shot, white background, lifestyle context'"
                  className="w-full bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-sm placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)] focus:outline-none resize-none" />
                <Button onClick={handleGenerate} disabled={generating || !prompt.trim()} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white w-full">
                  {generating ? <><CircleNotch size={16} className="animate-spin mr-2" /> Generating...</> : <><PaintBrush size={16} className="mr-2" /> Generate with {models?.models?.[selectedModel]?.name || selectedModel}</>}
                </Button>
              </div>
            </div>
            {result?.images?.[0] && (
              <div className="nx-metric-card rounded-lg animate-fade-in-up">
                <img src={`${API}/creative/image/${result.images[0].id}`} alt="Generated" className="w-full max-h-96 object-contain rounded mb-3" />
                <div className="flex items-center gap-2">
                  <span className="nx-tag nx-tag-blue">{result.model_name}</span>
                  <span className="nx-tag nx-tag-green">${result.cost}</span>
                  {result.ai_notes && <p className="text-xs text-[var(--text-secondary)] flex-1">{result.ai_notes.substring(0, 100)}</p>}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Campaign Builder */}
          <TabsContent value="campaign" className="space-y-4">
            <div className="nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <Target size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" /> Campaign Prompt Builder
              </h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Campaign Format</label>
                    <Select value={campFormat} onValueChange={setCampFormat}>
                      <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        {FORMAT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Platform</label>
                    <Select value={campPlatform} onValueChange={setCampPlatform}>
                      <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        {['Meta', 'Google', 'TikTok', 'YouTube', 'Pinterest'].map(p => <SelectItem key={p} value={p} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Product Description</label>
                  <textarea value={campProduct} onChange={e => setCampProduct(e.target.value)} rows={2}
                    placeholder="Describe the product, its key features, pricing, and unique selling points..."
                    className="w-full bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-sm placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)] focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Target Audience</label>
                  <Input value={campAudience} onChange={e => setCampAudience(e.target.value)}
                    placeholder="e.g., Women 25-44, urban professionals, interested in skincare"
                    className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                </div>
                <Button onClick={handleCampaignBrief} disabled={campGenerating || !campProduct.trim()} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white w-full">
                  {campGenerating ? <><CircleNotch size={16} className="animate-spin mr-2" /> Generating Brief...</> : <><FileText size={16} className="mr-2" /> Generate Campaign Brief (GPT-5-mini)</>}
                </Button>
              </div>
            </div>
            {campBrief?.brief && (
              <div className="nx-metric-card rounded-lg animate-fade-in-up">
                <div className="flex items-center gap-2 mb-3">
                  <span className="nx-tag nx-tag-blue">{campBrief.campaign_format?.replace(/_/g, ' ')}</span>
                  <span className="nx-tag nx-tag-green">{campBrief.platform}</span>
                  <span className="text-[10px] text-[var(--text-tertiary)] font-mono ml-auto">{campBrief.model}</span>
                </div>
                <pre className="text-xs text-[var(--text-secondary)] bg-black/20 p-4 rounded overflow-x-auto max-h-80 font-mono leading-relaxed whitespace-pre-wrap">
                  {typeof campBrief.brief === 'object' ? JSON.stringify(campBrief.brief, null, 2) : campBrief.brief}
                </pre>
              </div>
            )}
          </TabsContent>

          {/* Models & Video */}
          <TabsContent value="models" className="space-y-4">
            <div className="nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <PaintBrush size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" /> Image Models
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {models && Object.entries(models.models || {}).map(([k, v]) => (
                  <div key={k} className={`p-4 rounded border ${v.status === 'live' ? 'border-[var(--success-border)] bg-[var(--success-bg)]' : 'border-[var(--border)]'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-[var(--text-primary)] font-bold">{v.name}</span>
                      <span className={`nx-tag ${v.status === 'live' ? 'nx-tag-green' : 'nx-tag-gray'}`}>{v.status === 'live' ? 'Live' : 'Coming Soon'}</span>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] mb-2">{v.best_for}</p>
                    <p className="text-lg font-bold text-[var(--text-primary)] font-mono">${v.price_per_image}<span className="text-xs text-[var(--text-tertiary)]">/image</span></p>
                  </div>
                ))}
              </div>
            </div>
            <div className="nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <VideoCamera size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" /> Video Agents
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {models && Object.entries(models.video_agents || {}).map(([k, v]) => (
                  <div key={k} className="p-4 rounded border border-[var(--border)]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-[var(--text-primary)] font-bold">{v.name}</span>
                      <span className="nx-tag nx-tag-gray">{v.status === 'live' ? 'Live' : 'Coming Soon'}</span>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] mb-1">{v.capability}</p>
                    <p className="text-sm font-mono text-[var(--accent)]">{v.price}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Unit Economics */}
            {guardrails?.unit_economics && (
              <div className="nx-metric-card rounded-lg">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                  <CurrencyDollar size={16} className="inline mr-2 text-[var(--success)]" weight="duotone" /> Unit Economics
                </h3>
                <table className="nx-table">
                  <thead><tr><th>Task Type</th><th>Model</th><th>Cost/Call</th><th>Target Margin</th></tr></thead>
                  <tbody>
                    {Object.entries(guardrails.unit_economics).map(([k, v]) => (
                      <tr key={k}>
                        <td className="!text-[var(--text-primary)] capitalize">{k.replace(/_/g, ' ')}</td>
                        <td className="font-mono text-[var(--accent)]">{v.model}</td>
                        <td className="font-mono">${v.cost_per_call || v.cost_per_image}</td>
                        <td className="font-mono text-[var(--success)]">{v.margin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
